import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * FloatingTarotCards - 3 mystical tarot cards that glow when selected
 * Used in the Goals/Focus step for immersive selection
 */
export const FloatingTarotCards = ({ options, selected, onSelect }) => {
    const [hoveredCard, setHoveredCard] = useState(null);
    
    // Card back design patterns
    const cardPatterns = [
        "M10,10 L90,90 M90,10 L10,90", // X pattern
        "M50,10 L50,90 M10,50 L90,50", // Cross pattern
        "M50,10 Q90,50 50,90 Q10,50 50,10" // Diamond pattern
    ];
    
    return (
        <div className="flex justify-center items-end gap-4 sm:gap-6 h-72 sm:h-80">
            {options.map((option, index) => {
                const isSelected = selected === option.value;
                const isHovered = hoveredCard === index;
                const rotation = index === 0 ? -8 : index === 2 ? 8 : 0;
                const yOffset = index === 1 ? 0 : 20;
                
                return (
                    <motion.button
                        key={option.value}
                        onClick={() => onSelect(option.value)}
                        onMouseEnter={() => setHoveredCard(index)}
                        onMouseLeave={() => setHoveredCard(null)}
                        className="relative cursor-pointer focus:outline-none"
                        initial={{ opacity: 0, y: 50, rotate: rotation }}
                        animate={{ 
                            opacity: 1, 
                            y: yOffset,
                            rotate: rotation,
                            scale: isSelected ? 1.1 : isHovered ? 1.05 : 1
                        }}
                        transition={{ 
                            duration: 0.6, 
                            delay: index * 0.15,
                            scale: { duration: 0.2 }
                        }}
                        whileTap={{ scale: 0.95 }}
                        data-testid={`tarot-card-${option.value}`}
                    >
                        {/* Card glow effect */}
                        <AnimatePresence>
                            {(isSelected || isHovered) && (
                                <motion.div
                                    className="absolute -inset-2 rounded-lg"
                                    style={{
                                        background: isSelected 
                                            ? "radial-gradient(ellipse, rgba(212,175,55,0.4) 0%, transparent 70%)"
                                            : "radial-gradient(ellipse, rgba(212,175,55,0.2) 0%, transparent 70%)",
                                        filter: "blur(10px)"
                                    }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                />
                            )}
                        </AnimatePresence>
                        
                        {/* Card container */}
                        <motion.div
                            className="relative w-24 h-36 sm:w-28 sm:h-44 rounded-lg overflow-hidden"
                            style={{
                                background: "linear-gradient(145deg, #0A0A0B 0%, #1a1a1c 50%, #0A0A0B 100%)",
                                border: isSelected 
                                    ? "2px solid #D4AF37" 
                                    : "1px solid rgba(212,175,55,0.3)",
                                boxShadow: isSelected
                                    ? "0 0 30px rgba(212,175,55,0.4), 0 10px 40px rgba(0,0,0,0.8)"
                                    : "0 10px 30px rgba(0,0,0,0.6)"
                            }}
                            animate={{
                                y: isSelected ? [-2, 2, -2] : [0, -5, 0],
                                borderColor: isSelected 
                                    ? ["#D4AF37", "#FFD700", "#D4AF37"]
                                    : "rgba(212,175,55,0.3)"
                            }}
                            transition={{
                                y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                                borderColor: { duration: 2, repeat: Infinity }
                            }}
                        >
                            {/* Card back pattern */}
                            <svg 
                                viewBox="0 0 100 150" 
                                className="absolute inset-0 w-full h-full"
                                style={{ opacity: 0.15 }}
                            >
                                <defs>
                                    <pattern id={`cardPattern${index}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                        <circle cx="10" cy="10" r="1" fill="#D4AF37" />
                                    </pattern>
                                </defs>
                                <rect width="100" height="150" fill={`url(#cardPattern${index})`} />
                                <path 
                                    d={cardPatterns[index]} 
                                    stroke="#D4AF37" 
                                    strokeWidth="0.5" 
                                    fill="none"
                                    opacity="0.5"
                                />
                            </svg>
                            
                            {/* Mystical symbol in center */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                {/* Arcane circle */}
                                <motion.div
                                    className="relative w-14 h-14 sm:w-16 sm:h-16"
                                    animate={isSelected ? { rotate: 360 } : { rotate: 0 }}
                                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                >
                                    <svg viewBox="0 0 100 100" className="w-full h-full">
                                        <circle
                                            cx="50" cy="50" r="45"
                                            fill="none"
                                            stroke={isSelected ? "#D4AF37" : "rgba(212,175,55,0.4)"}
                                            strokeWidth="1"
                                            strokeDasharray="4 4"
                                        />
                                        <circle
                                            cx="50" cy="50" r="35"
                                            fill="none"
                                            stroke={isSelected ? "#D4AF37" : "rgba(212,175,55,0.3)"}
                                            strokeWidth="0.5"
                                        />
                                        {/* Star points */}
                                        {[0, 72, 144, 216, 288].map((deg) => (
                                            <line
                                                key={deg}
                                                x1="50"
                                                y1="50"
                                                x2={50 + 30 * Math.cos((deg * Math.PI) / 180)}
                                                y2={50 + 30 * Math.sin((deg * Math.PI) / 180)}
                                                stroke={isSelected ? "#D4AF37" : "rgba(212,175,55,0.3)"}
                                                strokeWidth="0.5"
                                            />
                                        ))}
                                    </svg>
                                    
                                    {/* Center icon */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <motion.span
                                            className="text-xl sm:text-2xl"
                                            style={{ 
                                                filter: isSelected ? "drop-shadow(0 0 8px rgba(212,175,55,0.8))" : "none"
                                            }}
                                            animate={isSelected ? {
                                                scale: [1, 1.1, 1],
                                                filter: [
                                                    "drop-shadow(0 0 8px rgba(212,175,55,0.8))",
                                                    "drop-shadow(0 0 15px rgba(212,175,55,1))",
                                                    "drop-shadow(0 0 8px rgba(212,175,55,0.8))"
                                                ]
                                            } : {}}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        >
                                            {option.icon}
                                        </motion.span>
                                    </div>
                                </motion.div>
                            </div>
                            
                            {/* Card corners */}
                            <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-primary/30" />
                            <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-primary/30" />
                            <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-primary/30" />
                            <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-primary/30" />
                        </motion.div>
                        
                        {/* Card label */}
                        <motion.p
                            className="mt-3 text-center font-cinzel text-xs sm:text-sm tracking-wider"
                            style={{
                                color: isSelected ? "#D4AF37" : "rgba(212,175,55,0.6)"
                            }}
                            animate={isSelected ? {
                                textShadow: [
                                    "0 0 10px rgba(212,175,55,0.5)",
                                    "0 0 20px rgba(212,175,55,0.8)",
                                    "0 0 10px rgba(212,175,55,0.5)"
                                ]
                            } : {}}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            {option.label}
                        </motion.p>
                    </motion.button>
                );
            })}
        </div>
    );
};

export default FloatingTarotCards;
